import React, { useEffect, useState } from 'react';
import { Box, FormControl, VStack, WarningIcon } from 'native-base';
import { useI18n, useSpringBigStore } from '@springbig/shared';
import { Caption, H3 } from './Typography';
import EmailVerificationForm from './EmailVerification/EmailVerificationForm';
import { useOffersScreen } from '@springbig/shared';
import { useGetMember } from '@springbig/shared';
import dayjs from 'dayjs';
import MockGiftCard from '~/shared/MockGiftCard';
import { Button, Modal } from './Theme';

type ClaimGiftCardModalProps = {
    rewardGrantId: number;
    closeModal: () => void;
}

const ClaimGiftCardModal = ({
    rewardGrantId,
    closeModal
}: ClaimGiftCardModalProps) => {

    const { claimGiftCard } = useOffersScreen();
    const { walletId, member, setMember, merchant } = useSpringBigStore();
    const { refetch: refreshMember } = useGetMember();
    const giftCardGrant = member?.reward_grants?.find((r) => r.id === rewardGrantId);
    const { t } = useI18n();

    const handleClaimGiftCardPressed = () => {

        claimGiftCard.mutate({ reward_grant_id: rewardGrantId });
    };

    const handleSuccessCloseModalPressed = () => {

        refreshMember();
        closeModal();
    };

    const updateMember = (validatedEmail: string): void => {

        setMember({
            ...member,
            consumer: {
                ...member.consumer,
                email: validatedEmail,
                email_validated: true
            }
        });
    };

    const emailVerifyForm = (
        <VStack space={4}>
            <H3 textAlign={'center'} maxW={'80%'}>
                { t('giftCard.claimEmailVerifyHeader', [merchant.gift_card_alias]) }
            </H3>

            <EmailVerificationForm
                t={t}
                walletId={walletId}
                onCancel={closeModal}
                onProceed={updateMember}
            />
        </VStack>
    );

    const claimGiftCardForm = (
        <VStack space={4}>
            <Box>
                <H3>
                    { giftCardGrant?.reward?.item_name }
                </H3>

                <Caption>
                    { t('giftCard.claimBefore', [dayjs(giftCardGrant?.expiration).format('MM/DD/YY')]) }
                </Caption>
            </Box>

            <MockGiftCard size="lg" amount={`$${giftCardGrant?.reward?.discount_value}`} />

            <FormControl isInvalid={claimGiftCard.isError}>
                <FormControl.ErrorMessage
                    marginBottom={2}
                    leftIcon={<WarningIcon size={5} />}
                >
                    { claimGiftCard.error?.data?.error }
                </FormControl.ErrorMessage>

                <Button
                    testID='alert-accept'
                    disabled={claimGiftCard.isLoading}
                    onPress={handleClaimGiftCardPressed}
                    text={ claimGiftCard.isLoading ? ('giftCard.claiming') : ('giftCard.claimNow') }
                />
            </FormControl>
        </VStack>
    );

    const  successPanel = (
        <VStack space={4}>
            <H3>
                { t('giftCard.claimSuccess', [merchant.gift_card_alias]) }
            </H3>

            <MockGiftCard size="lg" amount={`$${giftCardGrant?.reward?.discount_value}`} />

            <Button
                testID='alert-cancel'
                whiteSpace={'nowrap'}
                onPress={handleSuccessCloseModalPressed}
                variant='neutral'
                text={'redeem.go_back'}
            />
        </VStack>
    );

    const [currentModalBody, setCurrentModalBody] = useState('claim-form');

    useEffect(() => {

        if (claimGiftCard.isSuccess) {
            setCurrentModalBody('success-panel');
        }
        else if (member?.consumer?.email_validated) {
            setCurrentModalBody('claim-form');
        }
        else {
            setCurrentModalBody('email-verify-form');
        }
    }, [member, claimGiftCard.isSuccess]);

    if (!giftCardGrant) {
        return null;
    }

    return (
        <Modal py={4} isOpen={true} overflowY={'auto'} closeable={true} onClose={handleSuccessCloseModalPressed}>
            <Modal.Body>
                <Modal.Body
                    textAlign={'center'}
                    h={'100%'}
                >
                    {
                        currentModalBody === 'email-verify-form' ? emailVerifyForm :
                            currentModalBody === 'claim-form' ? claimGiftCardForm :
                                currentModalBody === 'success-panel' ? successPanel : null
                    }
                </Modal.Body>
            </Modal.Body>
        </Modal>
    );
};

export default ClaimGiftCardModal;
